import type { Camera } from '@babylonjs/core/Cameras/camera';
import '@babylonjs/core/Materials/standardMaterial';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { Scene } from '@babylonjs/core/scene';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { Button } from '@babylonjs/gui/2D/controls/button';
import { Control } from '@babylonjs/gui/2D/controls/control';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import type { ConnectionPane } from '../ui/connectionPane';
import { pagePose } from './floatingPages';

/** The pane's size in metres; at the floating pages' distance it reads like a road sign. */
const SIZE = { width: 0.9, height: 0.45 } as const;

/**
 * The panes of a dropped connection (D-29) in the headset, where the page's DOM does not reach:
 * the same words as on screen, drawn over everything in front of the player when one appears,
 * and the reclaim pane's OK for the controller's ray. Two triangles and one texture while shown.
 */
export function createConnectionPaneXr(scene: Scene, camera: Camera, onOk: () => void) {
  const plane = MeshBuilder.CreatePlane('connectionPane', SIZE, scene);
  // Above the arena HUD (1) and the immersive controls (2): nothing may hide it.
  plane.renderingGroupId = 3;
  plane.isPickable = true;
  plane.setEnabled(false);
  const texture = AdvancedDynamicTexture.CreateForMesh(plane, 1024, 512, false);
  const panel = new Rectangle('connectionPanePanel');
  panel.background = '#10140ef2';
  panel.color = '#d9bb79';
  panel.thickness = 6;
  panel.cornerRadius = 24;
  texture.addControl(panel);
  const line = (name: string, size: number, top: number, height: number) => {
    const block = new TextBlock(name, '');
    block.color = '#fff8dc';
    block.fontSize = size;
    block.textWrapping = true;
    block.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    block.top = `${top}px`;
    block.height = `${height}px`;
    block.width = '920px';
    panel.addControl(block);
    return block;
  };
  const title = line('connectionPaneTitle', 52, 36, 80);
  const text = line('connectionPaneText', 38, 130, 200);
  const ok = Button.CreateSimpleButton('connectionPaneOk', 'OK');
  ok.width = '240px';
  ok.height = '100px';
  ok.fontSize = 44;
  ok.color = '#fff8dc';
  ok.background = '#3d5a2c';
  ok.cornerRadius = 12;
  ok.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
  ok.top = '-36px';
  ok.onPointerClickObservable.add(onOk);
  panel.addControl(ok);
  let shown: ConnectionPane['kind'] | null = null;
  return {
    /** Call every frame with the pane to show in the headset, or none. */
    update(pane: ConnectionPane | null): void {
      if (!pane) {
        if (shown !== null) plane.setEnabled(false);
        shown = null;
        return;
      }
      // Placed afresh in front of the player whenever a different pane appears, then world-fixed.
      if (pane.kind !== shown) {
        const pose = pagePose(camera);
        plane.position.copyFrom(pose.position);
        plane.rotationQuaternion = pose.rotation;
        plane.setEnabled(true);
        shown = pane.kind;
      }
      if (title.text !== pane.title) title.text = pane.title;
      if (text.text !== pane.text) text.text = pane.text;
      ok.isVisible = pane.ok;
    },
    dispose(): void {
      texture.dispose();
      plane.dispose(false, true);
    },
  };
}
